<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import ConversationAction from 'dashboard/routes/dashboard/conversation/ConversationAction.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const assignmentDialogRef = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const inboxTitle = computed(
  () => inbox.value?.name || chatMetadata.value?.channel || ''
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const assignmentDialogTitle = computed(() =>
  [
    t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL'),
    t('CONVERSATION_SIDEBAR.TEAM_LABEL'),
    t('CONVERSATION.PRIORITY.TITLE'),
  ].join(' / ')
);

const openAssignmentDialog = () => {
  assignmentDialogRef.value?.open();
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-2 flex-1 w-full min-w-0 px-3 pt-2.5 pb-2"
  >
    <div
      class="flex flex-row gap-2 items-center justify-between w-full xl:gap-3 conversation-header__top-row"
    >
      <div
        class="flex items-center justify-start flex-1 max-w-full min-w-0 xl:max-w-[28rem] conversation-header__identity"
        :title="inboxTitle"
      >
        <BackButton
          v-if="showBackButton"
          :back-url="backButtonUrl"
          class="conversation-header__back ltr:mr-1 rtl:ml-1 xl:ltr:mr-2 xl:rtl:ml-2"
        />
        <Avatar
          :name="currentContact.name"
          :src="currentContact.thumbnail"
          :size="32"
          :status="currentContact.availability_status"
          :title="inboxTitle"
          hide-offline-status
          rounded-full
        />
        <div
          class="hidden sm:flex xl:hidden flex-col items-start flex-1 min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
        >
          <div
            class="flex flex-row items-center max-w-full gap-1 p-0 m-0 xl:hidden"
          >
            <span
              class="text-sm font-medium truncate leading-tight text-n-slate-12"
            >
              {{ currentContact.name }}
            </span>
            <fluent-icon
              v-if="!isHMACVerified"
              v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
              size="14"
              class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
              icon="warning"
            />
          </div>

          <div
            class="flex items-center gap-2 overflow-hidden text-xs conversation--header--actions text-ellipsis whitespace-nowrap xl:text-sm"
          >
            <InboxName :inbox="inbox" class="!mx-0" />
            <span v-if="isSnoozed" class="font-medium text-n-amber-10">
              {{ snoozedDisplayText }}
            </span>
          </div>
        </div>
        <Button
          v-if="currentChat.id"
          icon="i-lucide-user-cog"
          slate
          faded
          sm
          class="xl:!hidden !size-10 flex-shrink-0 ltr:ml-2 rtl:mr-2"
          :aria-label="assignmentDialogTitle"
          @click="openAssignmentDialog"
        />
      </div>
      <ConversationAction
        v-if="currentChat.id"
        variant="header"
        :conversation-id="currentChat.id"
        class="hidden xl:block flex-1 min-w-0 conversation-header__assignment"
      />
      <div
        class="flex flex-row items-center justify-end flex-shrink-0 gap-1.5 xl:gap-2 whitespace-nowrap header-actions-wrap conversation-header__actions"
      >
        <SLACardLabel
          v-if="hasSlaPolicyId"
          :chat="chat"
          show-extended-info
          :parent-width="width"
          class="hidden md:flex"
        />
        <MoreActions :conversation-id="currentChat.id" />
      </div>
    </div>

    <Dialog
      ref="assignmentDialogRef"
      width="md"
      position="top"
      :title="assignmentDialogTitle"
      :show-confirm-button="false"
      scroll-content
    >
      <ConversationAction
        v-if="currentChat.id"
        variant="header"
        :conversation-id="currentChat.id"
        class="assignment-dialog-action"
      />
    </Dialog>
  </div>
</template>

<style scoped>
@media (min-width: 1280px) {
  .conversation-header__top-row {
    display: grid;
    grid-template-columns: 2.75rem minmax(0, 1fr) max-content;
    align-items: center;
    column-gap: 0.75rem;
    min-height: 2.75rem;
    overflow: visible;
  }

  .conversation-header__identity {
    width: 2.75rem;
    min-width: 2.75rem;
    max-width: 2.75rem !important;
    overflow: visible;
    justify-content: center;
  }

  .conversation-header__assignment {
    width: 100%;
    max-width: none !important;
    align-self: center;
  }

  .conversation-header__actions {
    width: max-content;
    min-width: max-content;
    flex-wrap: nowrap;
    align-self: center;
  }

  .conversation-header__actions :deep(.actions--container) {
    gap: 0.5rem;
    flex-wrap: nowrap;
  }

  .conversation-header__actions :deep(.resolve-actions) {
    flex-wrap: nowrap;
  }
}

@media (min-width: 1536px) {
  .conversation-header__top-row {
    grid-template-columns: 2.75rem minmax(0, 1fr) max-content;
  }
}

@media (max-width: 1279px) {
  .conversation-header__back {
    width: 2.25rem;
    height: 2.5rem;
    flex-shrink: 0;
    justify-content: center;
    font-size: 0 !important;
  }

  .conversation-header__back :deep(i) {
    margin-left: 0;
    font-size: 1.5rem;
  }

  .header-actions-wrap :deep(.actions--container) {
    gap: 0.375rem;
  }
}
</style>
